import { Injectable } from '@angular/core';
import { createValidationRun } from '@epicuro-next/addons/sandbox';
import {
  ConfigurationsService,
  ConfigurationValue,
} from '@epicuro-next/platform/configuration';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import { isEqual } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Account, Product } from 'graphql-api/gql-api-interfaces';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeAll,
  sampleTime,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  ConfigStripStateConfigurationKeys,
  FEATURE_NAME,
} from '../../configurations';
import * as configStripActions from '../actions/index';
import {
  DeliveryItems,
  DeliveryItemsByPeriod,
  ProductSizeEntities,
  ProductVariantEntities,
} from '../models';
import { SalesUnitCondition } from '../models/sales-unit-condition.model';
import {
  selectDeliveryItemsWithSuppliedQuantity,
  selectSalesUnitConditions,
  selectSizeEntities,
  selectValidators,
  selectVariantEntities,
} from '../selectors';

@Injectable()
export class ConfigStripValidationsEffects {
  public validationRulesUponProductLoadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configStripActions.loadProductSuccess),
      switchMap((product) => {
        return this.configService
          .fetchConfigValue({
            namespace: FEATURE_NAME,
            featureName: ConfigStripStateConfigurationKeys.ValidatorFunctions,
            defaultValue: '[]',
            context: { product: product.product as Product },
          })
          .pipe(
            map((validators: ConfigurationValue) =>
              configStripActions.setValidators({
                validators: JSON.parse(validators as string) ?? [],
              }),
            ),
            catchError(() => {
              return of(configStripActions.setValidators({ validators: [] }));
            }),
          );
      }),
    ),
  );

  public deliveryItemsChanged$ = createEffect(() =>
    this.store.pipe(
      select(selectDeliveryItemsWithSuppliedQuantity),
      // subscription to selector as an effect sounds like a bad idea
      // it triggers an infinite actions chain
      // this is a patch to prevent triggering validation on each selector result when it's not changed
      distinctUntilChanged(isEqual),
      sampleTime(400),
      map(() => configStripActions.validateDeliveryItems({ validating: true })),
    ),
  );

  public validateDeliveryItems$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(
        ofType(configStripActions.validateDeliveryItems),
        filter(({ validating }) => validating),
      ),
      this.actions$.pipe(ofType(configStripActions.setValidators)),
    ]).pipe(
      withLatestFrom(
        this.store.pipe(select(selectActiveAccount)),
        this.store.pipe(select(selectDeliveryItemsWithSuppliedQuantity)),
        this.store.pipe(select(selectVariantEntities)),
        this.store.pipe(select(selectSizeEntities)),
        this.store.pipe(select(selectValidators)),
        this.store.pipe(select(selectSalesUnitConditions)),
      ),
      switchMap(
        ([
          ,
          account,
          deliveryItemsByShop,
          variants,
          sizes,
          validators,
          salesUnitConditions,
        ]) => {
          if (validators.length === 0) {
            return of(
              configStripActions.validateDeliveryItems({ validating: false }),
            );
          }

          const genericDeliveryItemsValidationFn = this.validateDeliveryItems({
            validators,
            account,
            variants,
            sizes,
            salesUnitConditions,
          });

          const allStoresAllPeriodsValidation = Object.entries(
            deliveryItemsByShop,
          )
            .map(
              ([deliveryShop, deliveryItemByPeriod]: [
                string,
                DeliveryItemsByPeriod,
              ]) =>
                Object.entries(deliveryItemByPeriod).map(
                  ([deliveryPeriod, deliveryItems]: [string, DeliveryItems]) =>
                    genericDeliveryItemsValidationFn({
                      deliveryItems,
                      deliveryPeriod,
                      deliveryShop,
                    }),
                ),
            )
            .flat(2)
            .filter(Boolean) as Observable<Action>[];

          if (allStoresAllPeriodsValidation.length === 0) {
            return of(
              configStripActions.validateDeliveryItems({ validating: false }),
            );
          }

          return forkJoin(allStoresAllPeriodsValidation).pipe(mergeAll());
        },
      ),
    ),
  );

  /** Dependencies */
  constructor(
    private actions$: Actions,
    private store: Store,
    private configService: ConfigurationsService,
    private logger: LoggerService,
  ) {}

  private validateDeliveryItems({
    validators,
    account,
    variants,
    sizes,
    salesUnitConditions,
  }: {
    validators: string[];
    account: Account | null;
    variants: ProductVariantEntities;
    sizes: ProductSizeEntities;
    salesUnitConditions: SalesUnitCondition[];
  }) {
    return ({
      deliveryItems,
      deliveryPeriod,
      deliveryShop,
    }: {
      deliveryItems: DeliveryItems;
      deliveryPeriod: string;
      deliveryShop: string;
    }) => {
      return forkJoin(
        validators.map((validatorFn: string) =>
          createValidationRun<string>(
            validatorFn,
            {
              account,
              deliveryItems,
              variants,
              sizes,
              salesUnitConditions,
            },
            this.logger,
          ),
        ),
      ).pipe(
        map((validationResults) => validationResults.flat()),
        // questionable actually either we have to return it as it
        // and as a result possible validation error with passed value
        catchError(() => of([])),
        map((validationErrors: string[]) => {
          return configStripActions.validateDeliveryItemsSuccess({
            validationErrors,
            deliveryPeriod,
            deliveryShop,
          });
        }),
      );
    };
  }
}
